import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faqAPI } from '@/services/api';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import FAQFeedback from '@/components/FAQFeedback';

export default function FAQPage() {
	const { t, i18n } = useTranslation();
	const [groups, setGroups] = useState([]);
	const [loading, setLoading] = useState(true);
	const [openId, setOpenId] = useState(null);
	const [query, setQuery] = useState('');

	useDocumentMeta({
		title: `${t('faq.title')} | Equi-Market`,
		description: t('faq.subtitle'),
	});

	useEffect(() => {
		setLoading(true);
		// Reduce locale to 2-letter ISO code (e.g. "en-US@posix" → "en", "pt-BR" → "pt")
		const lang = (i18n.language || 'en').toLowerCase().split(/[-_@]/)[0];
		faqAPI
			.listPublic(lang)
			.then((r) => setGroups(r.data?.groups || []))
			.catch(() => setGroups([]))
			.finally(() => setLoading(false));
	}, [i18n.language]);

	// Inject FAQPage JSON-LD structured data so Google can surface questions directly in SERPs.
	useEffect(() => {
		const id = 'faq-jsonld';
		document.getElementById(id)?.remove();
		const allItems = groups.flatMap((g) => g.items || []);
		if (allItems.length === 0) return;
		const ld = {
			'@context': 'https://schema.org',
			'@type': 'FAQPage',
			inLanguage: (i18n.language || 'en').toLowerCase().split(/[-_@]/)[0],
			mainEntity: allItems.map((it) => ({
				'@type': 'Question',
				name: it.question,
				acceptedAnswer: {
					'@type': 'Answer',
					text: it.answer,
				},
			})),
		};
		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.id = id;
		script.text = JSON.stringify(ld);
		document.head.appendChild(script);
		return () => { document.getElementById(id)?.remove(); };
	}, [groups, i18n.language]);

	const q = query.trim().toLowerCase();
	const filteredGroups = q
		? groups
			.map((g) => ({
				...g,
				items: g.items.filter(
					(it) => it.question.toLowerCase().includes(q) || it.answer.toLowerCase().includes(q)
				),
			}))
			.filter((g) => g.items.length > 0)
		: groups;

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="faq-page">
			{/* Header */}
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="flex items-center gap-3 mb-2">
						<HelpCircle className="w-6 h-6 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('faq.title')}
						</h1>
					</div>
					<p className="text-sm text-[#666055] mb-6 max-w-2xl">{t('faq.subtitle')}</p>
					<div className="relative max-w-xl">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666055]" />
						<Input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={t('faq.search_placeholder')}
							className="pl-11 h-12 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
							data-testid="faq-search-input"
						/>
					</div>
				</div>
			</div>

			{/* Body */}
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				{loading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<div key={i} className="h-14 bg-white border border-[#E6E2D6] rounded-sm img-skeleton" />
						))}
					</div>
				) : filteredGroups.length === 0 ? (
					<div className="text-center py-16 text-sm text-[#666055]" data-testid="faq-empty">
						{q ? t('faq.no_match') : t('faq.empty')}
					</div>
				) : (
					<div className="space-y-10">
						{filteredGroups.map((g) => {
							const catKey = g.category.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
							const catLabel = t(`faq.categories.${catKey}`, { defaultValue: g.category });
							return (
								<section key={g.category} data-testid={`faq-group-${g.category.replace(/\s+/g, '-')}`}>
									<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-4">{catLabel}</h2>
									<div className="bg-white border border-[#E6E2D6] rounded-sm divide-y divide-[#E6E2D6]">
									{g.items.map((item) => {
										const isOpen = openId === item.id;
										return (
											<div key={item.id} data-testid={`faq-item-${item.id}`}>
												<button
													type="button"
													onClick={() => setOpenId(isOpen ? null : item.id)}
													aria-expanded={isOpen}
													className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-[#F9F7F2]/50 transition-colors"
													data-testid={`faq-toggle-${item.id}`}
												>
													<span className="text-base font-medium text-[#1A3C34] font-['Playfair_Display']">
														{item.question}
													</span>
													<ChevronDown className={`w-5 h-5 text-[#8B4513] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
												</button>
												{isOpen && (
													<div className="px-5 pb-5 text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line"
															 data-testid={`faq-answer-${item.id}`}>
														{item.answer}
														<FAQFeedback faqId={item.id} />
													</div>
												)}
											</div>
										);
									})}
								</div>
							</section>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}